/**
 * Plain-text receipt renderers for DIRECT printing.
 *
 * The browser can never print without showing its dialog, so a direct print goes
 * through the local backend instead (POST /api/print), which spools the text to a
 * named Windows printer. Thermal printers render monospace text natively, so text
 * is the right wire format here — the HTML renderers in billPrinter.js /
 * kitchenPrinter.js stay as they are for on-screen previews and the fallback path.
 *
 * The same `format` config drives both, so a bill printed directly shows the same
 * fields as the HTML one.
 */

import { sanitizeCharges } from "./charges";
import { DEFAULT_BILL_FORMAT, wantsBigBillNo } from "./billPrinter";
import { DEFAULT_KITCHEN_FORMAT, isParcelOrder } from "./kitchenPrinter";

// Windows spools this through a GDI font that may not carry the ₹ glyph (U+20B9),
// and a missing glyph prints as a box on every line of every bill. "Rs." is safe
// on every printer. Switch this to "₹" if your printer's font handles it.
const RUPEE = "Rs.";

/*
| Emphasis (bold / double height)
|
| This receipt reaches the printer as RAW bytes, so bold is not a font choice —
| it is an ESC/POS command the printer obeys. The `font-weight: bold` in
| billPrinter.js only styles the admin preview and the browser-dialog fallback,
| which is why a bill that looked bold on screen printed flat on the roll.
|
| backend/scripts/print-text.ps1 already speaks ESC/POS (it sends the reset and
| the paper cut), and maps each character to one byte, so these pass straight
| through.
|
| Applied to WHOLE finished lines, never inside one: every column below is padded
| by string length, and a control character counted as a column would knock the
| money out of alignment.
*/
const ESC = "\x1b";
const GS = "\x1d";

/** Bold. Sends BOTH emphasis (ESC E) and double-strike (ESC G): many thermal
 *  printers honour only one of the two, so a ticket that "printed flat" with
 *  ESC E alone comes out properly bold when both are set. */
const bold = (line) => `${ESC}E\x01${ESC}G\x01${line}${ESC}G\x00${ESC}E\x00`;

// GS ! 0x01 — double HEIGHT only. Double width would halve the columns per line
// and wrap the header, so the big lines here grow downwards, not sideways.
const tall = (line) => `${GS}!\x01${line}${GS}!\x00`;

// GS ! 0x22 — TRIPLE width AND height, for a value that must carry right across a
// counter (the big Bill No. on its own line). Applied to a short run alone on its
// line, so the magnified glyphs never collide with a padded money column.
const huge = (line) => `${GS}!\x22${line}${GS}!\x00`;

/** Both, for the one or two lines that have to carry across a counter. */
const heading = (line) => bold(tall(line));

// Characters per line. 58mm thermal paper fits ~32, 80mm fits ~48.
function widthFor(paperSize) {
    return paperSize === "thermal-58" ? 32 : 48;
}

const repeat = (ch, n) => (n > 0 ? ch.repeat(n) : "");

function center(text, width) {
    const s = String(text || "");
    if (s.length >= width) return s.slice(0, width);
    const pad = Math.floor((width - s.length) / 2);
    return repeat(" ", pad) + s;
}

/** "Subtotal" + "Rs.250" pushed to opposite edges of the line. */
function lr(left, right, width) {
    const l = String(left || "");
    const r = String(right || "");
    const gap = width - l.length - r.length;
    if (gap < 1) {
        // Too long to sit on one line — clip the label, never the amount.
        const room = Math.max(0, width - r.length - 1);
        return `${l.slice(0, room)} ${r}`;
    }
    return l + repeat(" ", gap) + r;
}

/** Wrap a long item name onto continuation lines. */
function wrap(text, width) {
    const words = String(text || "").split(/\s+/).filter(Boolean);
    const lines = [];
    let cur = "";
    words.forEach((w) => {
        if (!cur.length) {
            cur = w;
        } else if ((cur + " " + w).length <= width) {
            cur += " " + w;
        } else {
            lines.push(cur);
            cur = w;
        }
    });
    if (cur.length) lines.push(cur);
    return lines.length ? lines : [""];
}

/** Pad a cell to `w`, right-aligned when `right`, clipping anything longer. */
function cell(text, w, right = false) {
    const s = String(text == null ? "" : text);
    if (s.length >= w) return s.slice(0, w);
    return right ? repeat(" ", w - s.length) + s : s + repeat(" ", w - s.length);
}

/**
 * Column widths for the No. / Item / Qty. / Price / Amount grid. The money
 * columns keep their width on a 58mm roll and the item name column absorbs the
 * difference, wrapping onto continuation lines.
 */
function itemCols(W) {
    const wide = W >= 40;
    const no = 3;
    const qty = wide ? 5 : 3;
    const price = wide ? 9 : 7;
    const amt = wide ? 10 : 8;
    return { no, qty, price, amt, item: W - no - qty - price - amt };
}

/** " 5%" for a line worth 5% of the subtotal, "" when it is not expressible. */
function percentOf(part, base) {
    if (!(Number(base) > 0) || !(Number(part) > 0)) return "";
    const p = Math.round((Number(part) / Number(base)) * 10000) / 100;
    if (!(p > 0)) return "";
    return ` ${Number.isInteger(p) ? p : p.toFixed(2)}%`;
}

// Two decimals by default. This renderer produces the slip that is spooled
// straight to the till printer, and it was rounding every line to whole rupees
// while the split-payment lines (the only caller passing dp explicitly) printed
// paise — so a receipt could read Subtotal 238 + GST 12 + Service 5 against a
// TOTAL of 254, and a split payment visibly failed to match its own total.
const money = (n, dp = 2) => `${RUPEE}${Number(n || 0).toFixed(dp)}`;

/**
 * The customer bill as text. Mirrors generateBillHtml's field logic so the two
 * cannot disagree about what a given format setting means.
 */
export function buildBillText({ order = {}, restaurant = {}, format = {} }) {
    const cfg = { ...DEFAULT_BILL_FORMAT, ...format };
    const W = widthFor(cfg.paper_size);
    const out = [];

    const items = order.items || [];
    const subtotal = Number(order.subtotal || 0);
    // A discount comes off the goods before tax — see billPrinter.js.
    const discount = Math.min(Math.max(Number(order.discount || 0), 0), subtotal);
    const taxBase = subtotal - discount;
    const tax = Number(order.tax || order.gst || 0);
    const serviceCharge = Number(order.service_charge || order.serviceCharge || 0);
    const charges = sanitizeCharges(order.charges || order.selectedCharges);
    const chargesTotal = charges.reduce((sum, c) => {
        if (c.charge_type === "Percentage") return sum + Math.round(taxBase * Number(c.amount) / 100);
        return sum + Number(c.amount || 0);
    }, 0);
    const grandTotal = Number(
        order.grand_total || order.total || (taxBase + tax + serviceCharge + chargesTotal)
    );

    const dateStr = order.date || new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const timeStr = order.time || new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    // No invented placeholder — see billPrinter.js. The line is dropped rather
    // than printing a number the bill does not have.
    const orderNumber = order.order_number || "";
    const tableName = order.tableName || order.table_name || (order.table_number ? `Table ${order.table_number}` : "Counter");
    const isParcel = isParcelOrder(order);
    const seatValue = order.table_number || String(tableName).replace(/^table\s*/i, "");
    const totalQty = items.reduce((sum, it) => sum + Number(it.quantity || 1), 0);
    const paymentMethod = order.payment_method || order.paymentMethod || "Cash";
    const paymentList = Array.isArray(order.payments) && order.payments.length ? order.payments : null;

    // ── Header ── (the emphasised block, matching the reference bill)
    if (cfg.header_title && cfg.header_title.trim()) {
        out.push(bold(center(cfg.header_title.trim().toUpperCase(), W)));
    }
    if (cfg.show_restaurant_name) {
        out.push(heading(center((restaurant.restaurant_name || "Restaurant").toUpperCase(), W)));
    }
    // GSTIN under the trading name, then the postal address — the order the
    // printed reference bill reads in.
    if (cfg.show_gst && restaurant.gst_number) out.push(bold(center(restaurant.gst_number, W)));
    const addr = [restaurant.address, restaurant.city, restaurant.state, restaurant.pincode]
        .filter((p) => p && String(p).trim())
        .join(", ");
    if (cfg.show_address && addr) wrap(addr, W).forEach((l) => out.push(bold(center(l, W))));
    if (cfg.show_phone && restaurant.mobile) out.push(center(`Ph: ${restaurant.mobile}`, W));
    if (cfg.show_email && restaurant.email) out.push(center(restaurant.email, W));
    if (cfg.show_fssai && restaurant.fssai_number) out.push(center(`FSSAI: ${restaurant.fssai_number}`, W));

    // A corrected/re-issued bill is marked so it is never mistaken for a second sale.
    if (order.isReprint) out.push(bold(center("* DUPLICATE / REPRINT *", W)));

    out.push(repeat("=", W));

    // ── Order meta: a name rule, then fields paired two to a line ──
    if (cfg.show_customer_name) {
        out.push(`Name: ${order.customer_name || ""}`.trimEnd());
        out.push(repeat("-", W));
    }

    // Tasty Travel prints the bill number BIG on its own line. Pulling it out of
    // the paired grid keeps the magnified glyphs clear of the string-padded
    // columns (see the emphasis note above). Every other shop keeps it inline.
    const bigBillNo = cfg.show_order_number && orderNumber && wantsBigBillNo(restaurant);

    const metaBits = [];
    if (cfg.show_date) metaBits.push(`Date: ${dateStr}`);
    // Tasty Travel layout: Date|Time on the first line, then Type|Bill No on the
    // SAME next line (Type on the left, the big Bill No on the right). So push Time
    // right after Date here, and the "Type:" line after it; the big Bill No then
    // rides on the right of that Type line (below). Other shops keep the old order
    // (Date|Type, Time|...), with the plain "Take Away" / "Dine In: <seat>".
    if (bigBillNo && cfg.show_time) metaBits.push(`Time: ${timeStr}`);
    if (cfg.show_table_name) {
        if (bigBillNo) metaBits.push(isParcel ? "Type: Take Away" : `Type: Dine-In ${seatValue}`);
        else metaBits.push(isParcel ? "Take Away" : `Dine In: ${seatValue}`);
    }
    if (!bigBillNo && cfg.show_time) metaBits.push(`Time: ${timeStr}`);
    if (cfg.show_waiter_name && (order.waiter_name || order.waiter)) metaBits.push(`Waiter: ${order.waiter_name || order.waiter}`);
    // cashier_label: a salon's front desk prints as "Receptionist".
    if (cfg.show_cashier_name && (order.cashier_name || order.cashier)) metaBits.push(`${order.cashier_label || "Cashier"}: ${order.cashier_name || order.cashier}`);
    if (cfg.show_order_number && orderNumber && !bigBillNo) metaBits.push(`Bill No.: ${orderNumber}`);
    // Salon bill: with ONE stylist for the whole bill, name them here as before.
    // With two or more, drop this line and name the stylist under each service below.
    const billStylists = [...new Set((items || []).map((i) => (i.stylist_name || "").trim()).filter(Boolean))];
    const multiStylist = billStylists.length > 1;
    if (order.stylist_name && !multiStylist) metaBits.push(`Stylist: ${order.stylist_name}`);

    // Tasty Travel nudges the right-hand column (Time, and the big Bill No under it)
    // a little further right than the usual 52%.
    const leftW = Math.ceil(W * (bigBillNo ? 0.58 : 0.52));
    const metaRows = [];
    for (let i = 0; i < metaBits.length; i += 2) {
        metaRows.push((cell(metaBits[i], leftW) + (metaBits[i + 1] || "")).trimEnd());
    }

    // Tasty Travel's big bill number rides on the RIGHT of the LAST date/time line
    // (right-aligned, triple size + bold) instead of a line of its own - so it sits
    // up beside the date/time with no gap above it. "Bill No" + the number travel
    // together; the label has no trailing dot. huge() is GS ! triple size, so each
    // digit prints ~3 columns wide; account for that when placing the group.
    if (bigBillNo) {
        const bnLabel = "Bill No: ";
        const idx = metaRows.length ? metaRows.length - 1 : 0;
        const left = metaRows[idx] || "";
        // Start the "Bill No:" group at the right-hand column - the SAME x as the
        // "Type:" value on the line above - so the two labels line up; the big
        // number then runs off to the right from there.
        const startCol = Math.max(left.length + 1, leftW);
        metaRows[idx] = left + repeat(" ", startCol - left.length) + bnLabel + bold(huge(orderNumber));
    }
    metaRows.forEach((r) => out.push(r));

    out.push(repeat("-", W));

    // ── Items: No. | Item | Qty. | Price | Amount ──
    // The money columns carry no currency mark, so they stay aligned on a
    // 32-column roll; only the grand total below is prefixed.
    const C = itemCols(W);
    out.push(bold(
        cell("No.", C.no) +
        cell("Item", C.item) +
        (cfg.show_item_qty ? cell("Qty.", C.qty, true) : repeat(" ", C.qty)) +
        (cfg.show_item_price ? cell("Price", C.price, true) : repeat(" ", C.price)) +
        cell("Amount", C.amt, true)
    ));
    out.push(repeat("-", W));

    items.forEach((it, idx) => {
        const qty = Number(it.quantity || 1);
        const rate = Number(it.price || 0);
        const lineTotal = rate * qty;
        const nameLines = wrap(it.item_name || it.name || "Item", C.item - 1);

        out.push(
            cell(`${idx + 1}`, C.no) +
            cell(nameLines[0], C.item) +
            (cfg.show_item_qty ? cell(qty, C.qty, true) : repeat(" ", C.qty)) +
            (cfg.show_item_price ? cell(rate.toFixed(2), C.price, true) : repeat(" ", C.price)) +
            cell(lineTotal.toFixed(2), C.amt, true)
        );
        // Continuation lines of a long name sit under the item column.
        nameLines.slice(1).forEach((l) => out.push(repeat(" ", C.no) + l));

        // On a multi-stylist bill, name who did this service under its line.
        if (multiStylist && (it.stylist_name || "").trim()) {
            out.push(repeat(" ", C.no) + `- ${it.stylist_name}`);
        }

        if (it.notes) wrap(`* ${it.notes}`, C.item - 1).forEach((l) => out.push(repeat(" ", C.no) + l));
    });

    out.push(repeat("-", W));

    // ── Summary ──
    const summaryRows = [];
    if (cfg.show_subtotal) summaryRows.push(["Sub Total", subtotal.toFixed(2)]);
    if (discount > 0) summaryRows.push([order.discount_label || "Discount", `-${discount.toFixed(2)}`]);
    if (cfg.show_tax && tax > 0) summaryRows.push([`GST${percentOf(tax, taxBase)}`, tax.toFixed(2)]);
    if (cfg.show_service_charge && serviceCharge > 0) {
        summaryRows.push([`Service Charge${percentOf(serviceCharge, taxBase)}`, serviceCharge.toFixed(2)]);
    }
    if (cfg.show_charges && charges.length > 0) {
        charges.forEach((c) => {
            const val = c.charge_type === "Percentage"
                ? Math.round(taxBase * Number(c.amount) / 100)
                : Number(c.amount);
            const label = c.charge_type === "Percentage" ? `${c.charge_name} ${Number(c.amount)}%` : c.charge_name;
            summaryRows.push([label, val.toFixed(2)]);
        });
    }

    // Total Qty prints alongside the first summary line, as on the reference bill.
    const qtyLabel = cfg.show_item_qty ? `Total Qty: ${totalQty}` : "";
    const moneyW = Math.min(W - 2, Math.max(18, C.qty + C.price + C.amt));
    summaryRows.forEach(([label, val], i) => {
        const left = i === 0 ? qtyLabel : "";
        out.push(cell(left, W - moneyW) + lr(label, val, moneyW));
    });

    if (cfg.show_grand_total) {
        out.push(repeat("=", W));
        out.push(heading(lr("Grand Total", money(grandTotal), W)));
        out.push(repeat("=", W));
    }

    // ── Payment ──
    if (cfg.show_payment_method) {
        if (paymentList && paymentList.length > 1) {
            out.push("Payment Split:");
            paymentList.forEach((p) => out.push(lr(`  ${p.method || "Cash"}`, money(p.amount, 2), W)));
        } else {
            out.push(lr("Payment Mode:", paymentMethod, W));
        }
    }

    // ── Footer ──
    if (cfg.footer_text && cfg.footer_text.trim()) {
        out.push("");
        wrap(cfg.footer_text.trim(), W).forEach((l) => out.push(center(l, W)));
    }
    if (cfg.terms_text && cfg.terms_text.trim()) {
        cfg.terms_text.split(/\r?\n/).forEach((raw) => {
            wrap(raw, W).forEach((l) => out.push(center(l, W)));
        });
    }

    return out.join("\n");
}

/**
 * The kitchen ticket as text. Big and sparse on purpose — a cook reads this
 * across a pass, so quantities lead and prices never appear.
 */
export function buildKotText({ order = {}, format = {} }) {
    const cfg = { ...DEFAULT_KITCHEN_FORMAT, ...format };
    const W = widthFor(cfg.paper_size);
    const out = [];

    const items = order.items || [];
    const timeStr = order.time || new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const orderNumber = order.order_number || "";

    const isParcel = isParcelOrder(order);
    const tableName = isParcel
        ? "PARCEL / TAKEAWAY"
        : (order.tableName || order.table_name || (order.table_number ? `Table ${order.table_number}` : "Dine-In"));

    // Compact kitchen ticket. No restaurant name/address/phone (the kitchen does
    // not need them) and no blank lines between items — just what the cook needs,
    // for roughly half the paper.

    out.push(repeat("=", W));
    // Table (or PARCEL) banner — the first thing the cook reads, big + bold.
    out.push(heading(center(
        isParcel ? "*** PARCEL ***" : `*** ${String(tableName).toUpperCase()} ***`,
        W
    )));
    out.push(repeat("=", W));

    // The order number on every ticket: a parcel calls it a Token (to match the
    // packed order at pickup), a dine-in calls it the Bill No. — so the kitchen
    // can call an order by the same number that's on the customer's bill.
    // The token number, joined on one line and printed BIG + bold (double height),
    // so the kitchen can call the order by the number that's also on the bill.
    if (orderNumber) out.push(heading(center(`Token No: ${orderNumber}`, W)));

    // Who took the order (bold) on the left, time on the right — one line. A
    // waiter's dine-in order names the waiter; a counter order the cashier rang
    // up names the cashier.
    const waiter = order.waiter_name || order.waiter;
    const cashier = order.cashier_name || order.cashier;
    const takenBy = waiter ? `Waiter: ${waiter}` : (cashier ? `${order.cashier_label || "Cashier"}: ${cashier}` : "");
    out.push(bold(lr(takenBy, timeStr, W)));

    out.push(repeat("-", W));
    const showQty = cfg.show_item_qty !== 0;   // default on
    // ITEM on the left, QTY as a column header — but the column is pulled IN from
    // the far paper edge so the number sits NEAR the item, not stranded at the
    // right margin. The number for each item lines up under that header, with a
    // blank line between items.
    const QW = showQty ? Math.max(20, Math.round(W * 0.62)) : W;   // qty column width (nearer the items)
    out.push(bold(showQty ? lr("ITEM", "QTY", QW) : "ITEM"));
    out.push(repeat("-", W));
    out.push("");   // breathing room before the first item

    let totalQty = 0;
    items.forEach((it) => {
        const qty = Number(it.quantity || 1);
        totalQty += qty;
        const name = it.item_name || it.name || "Item";
        const qtyStr = showQty ? `${qty}` : "";
        const reserve = showQty ? qtyStr.length + 1 : 0;   // keep the name clear of the qty column
        const nameLines = wrap(name, QW - reserve);
        nameLines.forEach((l, i) => {
            const isLast = i === nameLines.length - 1;
            out.push(bold(showQty && isLast ? lr(l, qtyStr, QW) : l));   // number under the QTY header
        });
        // A cooking note matters, so keep it right under its item.
        if (cfg.show_item_notes && (it.notes || it.note)) {
            wrap(`** ${it.notes || it.note}`, W).forEach((l) => out.push(bold(l)));
        }
        // No blank line between items — they sit together (only the one gap after
        // the header, above the first item).
    });

    out.push(repeat("-", W));
    out.push(bold(`Total: ${items.length} items / ${totalQty} pcs`));

    return out.join("\n");
}

/** The short slip behind the Test print button on the cashier Printer page. */
export function buildTestText({ printerName = "", role = "", restaurantName = "", paperSize = "thermal" } = {}) {
    const W = widthFor(paperSize);
    const now = new Date();
    const out = [];

    out.push(heading(center("PRINTER TEST", W)));
    if (restaurantName) out.push(bold(center(restaurantName.toUpperCase(), W)));
    out.push(repeat("=", W));
    out.push(lr("Printer:", printerName || "(not set)", W));
    if (role) out.push(lr("Prints:", role, W));
    out.push(lr("Date:", now.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }), W));
    out.push(lr("Time:", now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }), W));
    out.push(repeat("=", W));
    out.push(center("If you can read this,", W));
    out.push(center("the printer works.", W));
    out.push("");
    // The title above is emphasised and double height. If these two lines look
    // identical on the slip, the printer is ignoring ESC/POS emphasis and the
    // bold on the bill will not come out either — which is the one thing a test
    // print should be able to tell you.
    out.push(bold(center("This line should be BOLD.", W)));
    out.push(center("This line should be normal.", W));
    out.push("");
    out.push(center("InWallz POS", W));

    return out.join("\n");
}

const receiptText = { buildBillText, buildKotText, buildTestText };

export default receiptText;
